import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { categoryLabel } from '../components/CategoryPicker'
import { Screen } from '../components/Layout'
import {
  Balance,
  Button,
  Card,
  EmptyState,
  ErrorBanner,
  Field,
  Spinner,
  balanceSentence,
  inputClass,
} from '../components/ui'
import { money, parseAmount, shortDate, timestamp } from '../lib/format'
import {
  useCategories,
  useMarkSeen,
  useRemoveMember,
  useRemoveSplit,
  useSettle,
  useSplitLog,
  useSplits,
  useUpdateSplit,
} from '../lib/queries'
import type { Split } from '../lib/types'

/**
 * One pair: the split list, the net, and the settle button (requirement 6).
 *
 * Settling sends back `netMinor` and `nextSeq` exactly as the list returned them.
 * They are the watermark: the server refuses the settlement if either moved, so a
 * split the other person added while this screen sat open cannot be swallowed
 * into a settlement that never counted it. That is also why neither value is
 * recomputed here from the rows on screen.
 */
export function PairDetailPage() {
  const { pairId } = useParams<{ pairId: string }>()
  const [state, setState] = useState<'open' | 'all'>('open')
  const splits = useSplits(pairId, state)
  const taxonomy = useCategories()
  const settle = useSettle(pairId ?? '')
  const markSeen = useMarkSeen(pairId ?? '')
  const [confirmSettle, setConfirmSettle] = useState(false)
  const [confirmRemove, setConfirmRemove] = useState(false)
  const removeMember = useRemoveMember(pairId ?? '')
  const navigate = useNavigate()

  // Clear the unseen marker once, on arrival. Re-firing it on every refetch would
  // write to the pair partition on a timer for a screen nobody is looking at, and
  // a closed pair rejects the write outright -- it is history, not a live ledger.
  const seenSent = useRef(false)
  useEffect(() => {
    if (pairId && splits.data && !splits.data.closedAt && !seenSent.current) {
      seenSent.current = true
      markSeen.mutate()
    }
  }, [pairId, splits.data, markSeen])

  if (!pairId) return null
  if (splits.isLoading) {
    return (
      <Screen title="分帳">
        <Spinner />
      </Screen>
    )
  }
  if (!splits.data) {
    return (
      <Screen title="分帳">
        <ErrorBanner error={splits.error} onRetry={() => void splits.refetch()} />
        <Link to="/members" className="mt-4 block text-sm text-accent">
          回到成員列表
        </Link>
      </Screen>
    )
  }

  const data = splits.data
  const open = data.splits.filter((s) => !s.settled)
  /**
   * Splits above the watermark, including the ones with nothing left to show.
   *
   * A cancelled split keeps its sequence -- the watermark needs them contiguous --
   * so the watermark can sit behind while the list looks empty. That is the state
   * `settledUp` refuses, and keying the 結清 button on the visible rows instead
   * hid the only way out of it: net zero, nothing on screen, 移除成員 disabled
   * with no button left to press.
   */
  const outstanding = data.nextSeq - 1 - data.settledThroughSeq > 0
  // The same rule the server enforces as a DynamoDB condition, mirrored here only
  // to choose what to render. The button is never the thing that decides: press
  // it on a stale screen and the request 409s.
  const settledUp = data.netMinor === 0 && !outstanding

  return (
    <Screen
      title={data.displayName}
      action={
        <button
          type="button"
          className="text-sm text-accent"
          onClick={() => setState(state === 'open' ? 'all' : 'open')}
        >
          {state === 'open' ? '全部紀錄' : '只看未結'}
        </button>
      }
    >
      <div className="space-y-4">
        <ErrorBanner error={settle.error} onRetry={() => void splits.refetch()} />

        {data.closedAt ? (
          <Card className="p-4">
            <p className="text-sm">已移除這位成員。</p>
            <p className="mt-1 text-xs text-muted">
              以下是結清後保留的歷史紀錄，不能再修改，也不能再新增分帳。要重新開始分帳請重新邀請一次。
            </p>
          </Card>
        ) : null}

        <Card className="p-4">
          <p className="text-sm text-muted">目前結餘</p>
          <p className="mt-1">
            <Balance
              netMinor={data.netMinor}
              currency={data.currency}
              className="text-3xl font-semibold"
            />
          </p>
          <p className="mt-1 text-sm text-muted">
            {balanceSentence(data.netMinor, data.currency, data.displayName)}
          </p>
          {data.status === 'pending' ? (
            <p className="mt-2 text-xs text-muted">
              {data.displayName} 還沒登入過，登入後就會看到這些分帳。
            </p>
          ) : null}
          {data.lastSeen ? (
            <p className="mt-1 text-xs text-muted">對方最後查看：{timestamp(data.lastSeen)}</p>
          ) : null}
        </Card>

        {data.netMinor !== 0 || outstanding ? (
          confirmSettle ? (
            <Card className="space-y-3 p-4">
              <p className="text-sm">
                {open.length === 0 && data.netMinor === 0
                  ? // Everything outstanding was cancelled, so there is no money to
                    // move -- but the ledger still has to be closed off before the
                    // member can be removed.
                    '目前沒有金額需要收付。結清後這段紀錄就固定下來，也才能移除成員。'
                  : `把目前的 ${open.length} 筆一次結清${
                      data.netMinor === 0
                        ? '（金額剛好互相抵銷）'
                        : data.netMinor > 0
                          ? `，${data.displayName} 付你 ${money(data.netMinor, data.currency)}`
                          : `，你付 ${data.displayName} ${money(-data.netMinor, data.currency)}`
                    }。結清後這些金額就不能再修改。`}
              </p>
              <div className="flex gap-2">
                <Button
                  className="flex-1"
                  disabled={settle.isPending}
                  onClick={() =>
                    settle.mutate(
                      { expectedNetMinor: data.netMinor, expectedNextSeq: data.nextSeq },
                      { onSuccess: () => setConfirmSettle(false) },
                    )
                  }
                >
                  {settle.isPending ? '結清中…' : '確定結清'}
                </Button>
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={() => setConfirmSettle(false)}
                >
                  取消
                </Button>
              </div>
            </Card>
          ) : (
            <Button className="w-full" onClick={() => setConfirmSettle(true)}>
              結清
            </Button>
          )
        ) : null}

        {data.splits.length === 0 ? (
          <EmptyState
            title={state === 'open' ? '沒有未結的分帳' : '還沒有分帳紀錄'}
            hint="記帳時選擇分帳，就會出現在這裡。"
          />
        ) : (
          <ul className="space-y-2">
            {data.splits.map((split) => (
              <SplitRow
                key={split.seq}
                pairId={pairId}
                split={split}
                theirName={data.displayName}
                categoryText={categoryLabel(taxonomy.data, split.categoryId, split.subCategoryId)}
              />
            ))}
          </ul>
        )}

        {data.closedAt ? null : (
          <Card className="space-y-3 p-4">
            <ErrorBanner error={removeMember.error} onRetry={() => void splits.refetch()} />
            {confirmRemove ? (
              <>
                <p className="text-sm">
                  把 {data.displayName} 從分帳成員中移除？雙方的分帳清單都會消失，已結清的紀錄會保留在
                  原本的支出裡。
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="danger"
                    className="flex-1"
                    disabled={removeMember.isPending}
                    onClick={() =>
                      removeMember.mutate(undefined, {
                        onSuccess: () => {
                          setConfirmRemove(false)
                          void navigate('/members')
                        },
                      })
                    }
                  >
                    {removeMember.isPending ? '移除中…' : '確定移除'}
                  </Button>
                  <Button
                    variant="secondary"
                    className="flex-1"
                    onClick={() => setConfirmRemove(false)}
                  >
                    取消
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Button
                  variant="danger"
                  className="w-full"
                  disabled={!settledUp}
                  onClick={() => setConfirmRemove(true)}
                >
                  移除成員
                </Button>
                <p className="text-xs text-muted">
                  {settledUp
                    ? '移除後雙方都看不到這個分帳清單，支出本身會留在各自的帳本裡。'
                    : '還有未結清的分帳，先結清才能移除成員。'}
                </p>
              </>
            )}
          </Card>
        )}
      </div>
    </Screen>
  )
}

/**
 * One split, expandable into an edit form and a change log.
 *
 * Either person can change an unsettled share (design D11), so the row shows who
 * touched it last -- otherwise a number moving on its own looks like a bug.
 */
function SplitRow({
  pairId,
  split,
  theirName,
  categoryText,
}: {
  pairId: string
  split: Split
  theirName: string
  categoryText: string
}) {
  const [expanded, setExpanded] = useState(false)
  const [share, setShare] = useState(String(split.shareMinor))
  const update = useUpdateSplit(pairId)
  const remove = useRemoveSplit(pairId)
  const log = useSplitLog(pairId, expanded ? split.seq : undefined)

  const shareMinor = parseAmount(share, split.currency)
  const invalid = shareMinor !== undefined && shareMinor > split.totalMinor

  return (
    <li className="rounded-2xl bg-ink-soft/70 ring-1 ring-ink-line/60">
      <button
        type="button"
        className="flex w-full items-center gap-3 p-4 text-left"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="min-w-0 flex-1">
          {/*
            The note first: it is what the payer actually typed about this
            expense, and "生日蛋糕" settles an argument that "餐飲" starts. The
            category is the fallback, not the headline -- it is a bucket, and the
            amount and date beside it already carry more information. Splits
            recorded before the note was snapshotted have none, and fall back
            exactly as they did before.
          */}
          <p className="truncate text-sm">{split.note ?? split.merchant ?? categoryText}</p>
          <p className="truncate text-xs text-muted">
            {shortDate(split.date)} · 共 {money(split.totalMinor, split.currency)} ·{' '}
            {split.viewerIsPayer ? '你先付' : `${theirName} 先付`}
            {split.isCustomShare ? ' · 自訂' : ''}
            {split.settled ? ' · 已結清' : ''}
          </p>
        </div>
        <Balance netMinor={split.viewerNetMinor} currency={split.currency} className="text-sm" />
      </button>

      {expanded ? (
        <div className="space-y-3 border-t border-ink-line/40 p-4">
          <ErrorBanner error={update.error ?? remove.error} />
          <p className="text-xs text-muted">
            {theirName} 負擔 {money(split.shareMinor, split.currency)} · 最後更新{' '}
            {timestamp(split.updatedAt)}
          </p>

          {split.settled ? (
            <p className="text-xs text-muted">已結清的分帳不能修改。如需調整，請新增一筆分帳。</p>
          ) : (
            <>
              <Field label={`${theirName} 負擔金額`}>
                <input
                  className={`${inputClass} tnum py-2`}
                  inputMode="decimal"
                  value={share}
                  onChange={(e) => setShare(e.target.value)}
                />
              </Field>
              {invalid ? (
                <p className="text-xs text-owing">不能超過這筆支出的總金額。</p>
              ) : null}
              <div className="flex gap-2">
                <Button
                  className="flex-1 py-2 text-sm"
                  disabled={
                    shareMinor === undefined ||
                    invalid ||
                    shareMinor === split.shareMinor ||
                    update.isPending
                  }
                  onClick={() => {
                    if (shareMinor === undefined) return
                    update.mutate({
                      seq: split.seq,
                      shareMinor,
                      expectedRevision: split.revision,
                    })
                  }}
                >
                  {update.isPending ? '儲存中…' : '儲存'}
                </Button>
                <Button
                  variant="danger"
                  className="flex-1 py-2 text-sm"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate({ seq: split.seq, expectedRevision: split.revision })}
                >
                  取消分帳
                </Button>
              </div>
              <p className="text-xs text-muted">
                「取消分帳」只解除這筆分帳，支出本身會留在你的帳本裡。
              </p>
            </>
          )}

          <Link to={`/expenses/${split.expenseId}`} className="block text-sm text-accent">
            查看原始支出
          </Link>

          {log.data && log.data.length > 1 ? (
            <ul className="space-y-1 border-t border-ink-line/40 pt-3 text-xs text-muted">
              {log.data.map((entry, i) => (
                <li key={`${entry.at}-${i}`}>
                  {timestamp(entry.at)} ·{' '}
                  {entry.from === null
                    ? `建立，${money(entry.to, split.currency)}`
                    : `${money(entry.from, split.currency)} → ${money(entry.to, split.currency)}`}
                  {entry.cause === 'expense-edit' ? '（因支出金額變更）' : ''}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </li>
  )
}
